import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IonAlert, IonFab, IonFabButton, IonFabList, IonIcon, IonSpinner } from '@ionic/react';
import {
  add,
  appsOutline,
  checkmarkOutline,
  contractOutline,
  expandOutline,
  pencilOutline,
  removeOutline,
  reorderFourOutline,
  refreshOutline,
  megaphoneOutline,
  checkmarkCircle,
  cartOutline,
} from 'ionicons/icons';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import {
  addWidget,
  type Dashboard,
  type DashboardWidget,
  type SectionConfig,
  deleteWidget,
  getDashboardFull,
  getWidgetDefinitions,
  getRootWidgets,
  getWidgetsByColumn,
  setWidgetParent,
  subscribeToWidgets,
  updateWidget,
  updateWidgetSortOrders,
  type WidgetCategory,
  type WidgetDefinition,
} from '../services/dashboard';
import SortableWidget from './SortableWidget';
import SectionWidget from './widgets/SectionWidget';
import WidgetConfigForm from './WidgetConfigForm';
import DraggableWidgetOption from './DraggableWidgetOption';
import DashboardFAB, { type FABAction } from './DashboardFAB';
import { WidgetFullscreenOverlay } from './ui/WidgetFullscreenOverlay';

// UI Components - following IonicLayout.md standards
import { PageShell } from './ui/PageShell';
import { Modal, ModalSection } from './ui/Modal';
import { Search } from './ui/Search';
import { Segment, type SegmentOption } from './ui/Segment';

import './DashboardDisplay.css';

// Widget category options for the picker
const WIDGET_CATEGORY_OPTIONS: SegmentOption<WidgetCategory | 'all'>[] = [
  { value: 'all', label: 'All' },
  { value: 'layout', label: 'Layout' },
  { value: 'time', label: 'Time' },
  { value: 'calendar', label: 'Calendar' },
  { value: 'tasks', label: 'Tasks' },
  { value: 'family', label: 'Family' },
  { value: 'media', label: 'Media' },
  { value: 'info', label: 'Info' },
  { value: 'travel', label: 'Travel' },
];

interface DashboardDisplayProps {
  dashboardId: string;
  householdId: string;
  editMode?: boolean;
  onEditModeChange?: (editing: boolean) => void;
  onSettingsClick?: () => void;
}

const DashboardDisplay: React.FC<DashboardDisplayProps> = ({
  dashboardId,
  householdId,
  editMode: controlledEditMode,
  onEditModeChange,
  onSettingsClick,
}) => {
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [widgets, setWidgets] = useState<DashboardWidget[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Support both controlled and uncontrolled edit mode
  const [internalEditMode, setInternalEditMode] = useState(false);
  const editMode = controlledEditMode !== undefined ? controlledEditMode : internalEditMode;

  const handleEditModeToggle = () => {
    const newEditMode = !editMode;
    if (onEditModeChange) {
      onEditModeChange(newEditMode);
    } else {
      setInternalEditMode(newEditMode);
    }
  };

  // Widget picker state
  const [showWidgetPicker, setShowWidgetPicker] = useState(false);
  const [widgetDefs, setWidgetDefs] = useState<WidgetDefinition[]>([]);
  const [widgetCategory, setWidgetCategory] = useState<WidgetCategory | 'all'>('all');
  const [widgetSearch, setWidgetSearch] = useState('');

  // Container section state
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  // dnd-kit state
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeDragData, setActiveDragData] = useState<{
    type: 'existing-widget' | 'new-widget';
    widgetType?: string;
    widgetDef?: WidgetDefinition;
  } | null>(null);

  // dnd-kit sensors - require a small drag distance before activating
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Alert state
  const [deleteAlert, setDeleteAlert] = useState<{ isOpen: boolean; widgetId: string | null }>({
    isOpen: false,
    widgetId: null,
  });

  // Widget configuration state
  const [configWidget, setConfigWidget] = useState<DashboardWidget | null>(null);

  // Quick action modal states (for view mode FAB)
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showChoreModal, setShowChoreModal] = useState(false);
  const [showGroceryModal, setShowGroceryModal] = useState(false);

  // Fullscreen widget state
  const [fullscreenWidgetId, setFullscreenWidgetId] = useState<string | null>(null);
  const fullscreenWidget = fullscreenWidgetId ? widgets.find(w => w.id === fullscreenWidgetId) || null : null;

  // Get root widgets (sorted by sort_order)
  const rootWidgets = useMemo(() => getRootWidgets(widgets), [widgets]);

  // Get available sections for "Move to Section" menu
  const availableSections = useMemo(() => {
    return rootWidgets
      .filter(w => w.widget_type === 'section')
      .map(w => ({
        id: w.id,
        label: ((w.config as Partial<SectionConfig>)?.label) || 'Untitled Section',
      }));
  }, [rootWidgets]);

  // Load dashboard
  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      const data = await getDashboardFull(dashboardId);
      if (data) {
        setDashboard(data.dashboard);
        setWidgets(data.widgets);
      }
      setLoading(false);
    };

    loadDashboard();

    // Subscribe to real-time updates
    const unsubscribe = subscribeToWidgets(dashboardId, (updatedWidgets) => {
      setWidgets(updatedWidgets);
    });

    return () => unsubscribe();
  }, [dashboardId]);

  // Auto-refresh
  useEffect(() => {
    if (!dashboard?.refresh_interval) return;

    const interval = setInterval(() => {
      setWidgets(prev => [...prev]);
    }, dashboard.refresh_interval * 1000);

    return () => clearInterval(interval);
  }, [dashboard?.refresh_interval]);

  // Load widget definitions for the picker
  useEffect(() => {
    const loadWidgetDefs = async () => {
      const defs = await getWidgetDefinitions();
      setWidgetDefs(defs);
    };
    loadWidgetDefs();
  }, []);

  // Filter widgets for the picker
  const filteredWidgets = widgetDefs.filter(w => {
    if (widgetCategory !== 'all' && w.category !== widgetCategory) return false;
    if (widgetSearch && !w.name.toLowerCase().includes(widgetSearch.toLowerCase())) return false;
    return true;
  });

  // Get max sort_order for root widgets
  const getMaxSortOrder = () => {
    if (rootWidgets.length === 0) return 0;
    return Math.max(...rootWidgets.map(w => w.sort_order ?? 0)) + 1;
  };

  // Add a widget to the dashboard
  const handleAddWidget = async (widgetType: string) => {
    const def = widgetDefs.find(w => w.id === widgetType);
    const newWidget = await addWidget(dashboardId, {
      widget_type: widgetType,
      grid_x: 0,
      grid_y: 0,
      grid_w: def?.default_width || 4,
      grid_h: def?.default_height || 2,
      sort_order: getMaxSortOrder(),
    });

    if (newWidget) {
      setWidgets(prev => [...prev, newWidget]);
    }
    setShowWidgetPicker(false);
  };

  // Add a layout widget (section or spacer)
  const handleAddLayoutWidget = async (widgetType: 'section' | 'spacer') => {
    const defaultConfig = widgetType === 'section'
      ? { label: 'New Section', columnCount: 1, collapsible: true, defaultExpanded: true }
      : { height: 1 };

    const newWidget = await addWidget(dashboardId, {
      widget_type: widgetType,
      grid_x: 0,
      grid_y: 0,
      grid_w: 12,
      grid_h: widgetType === 'section' ? 3 : 1,
      config: defaultConfig,
      sort_order: getMaxSortOrder(),
    });

    if (newWidget) {
      setWidgets(prev => [...prev, newWidget]);
    }
  };

  // Toggle section collapse
  const handleToggleSection = (sectionId: string, expanded: boolean) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (expanded) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  // Handle moving a widget into/out of a section
  const handleMoveToSection = useCallback(async (widgetId: string, sectionId: string | null, columnIndex: number = 0) => {
    // Calculate the new sort_order based on target location
    let newSortOrder: number;
    if (sectionId) {
      // Moving into a section - get the next sort_order in that column
      const targetColumnWidgets = getWidgetsByColumn(widgets, sectionId, columnIndex);
      newSortOrder = targetColumnWidgets.length;
    } else {
      // Moving to root level - get the next sort_order for root widgets
      newSortOrder = rootWidgets.length;
    }

    const success = await setWidgetParent(widgetId, sectionId, columnIndex, newSortOrder);
    if (success) {
      setWidgets(prev => prev.map(w =>
        w.id === widgetId
          ? {
              ...w,
              parent_widget_id: sectionId,
              column_index: sectionId ? columnIndex : null,
              sort_order: newSortOrder,
            }
          : w
      ));
    }
  }, [widgets, rootWidgets]);

  // Handle reordering widgets (within sections)
  const handleReorderWidgets = useCallback(async (updates: Array<{ id: string; sort_order: number; column_index?: number }>) => {
    // Update local state immediately
    setWidgets(prev => prev.map(w => {
      const update = updates.find(u => u.id === w.id);
      if (update) {
        return {
          ...w,
          sort_order: update.sort_order,
          column_index: update.column_index ?? w.column_index,
        };
      }
      return w;
    }));

    // Save to database
    const success = await updateWidgetSortOrders(updates);
    if (!success) {
      console.error('Failed to save widget reorder to database:', updates);
    }
  }, []);

  // Handle deleting a widget
  const handleDeleteWidget = (widgetId: string) => {
    setDeleteAlert({ isOpen: true, widgetId });
  };

  const confirmDeleteWidget = async () => {
    if (!deleteAlert.widgetId) return;
    const success = await deleteWidget(deleteAlert.widgetId);
    if (success) {
      setWidgets(prev => prev.filter(w => w.id !== deleteAlert.widgetId));
    }
    setDeleteAlert({ isOpen: false, widgetId: null });
  };

  // Handle updating a widget
  const handleUpdateWidget = async (widgetId: string, updates: Partial<DashboardWidget>) => {
    const updated = await updateWidget(widgetId, updates);
    if (updated) {
      setWidgets(prev => prev.map(w =>
        w.id === widgetId ? { ...w, ...updates } : w
      ));
    }
  };

  // Handle configure widget
  const handleConfigureWidget = (widgetId: string) => {
    const widget = widgets.find(w => w.id === widgetId);
    if (widget) {
      setConfigWidget(widget);
    }
  };

  // Handle expand widget to fullscreen
  const handleExpandWidget = (widgetId: string) => {
    setFullscreenWidgetId(widgetId);
  };

  const handleSaveConfig = async (newConfig: Record<string, unknown>) => {
    if (!configWidget) return;
    await handleUpdateWidget(configWidget.id, { config: newConfig });
    setConfigWidget(null);
  };

  // Root droppable zone for cross-section drops
  const { setNodeRef: setRootDropRef, isOver: isOverRoot } = useDroppable({
    id: 'dashboard-root',
    disabled: !editMode,
    data: { type: 'root' },
  });

  // dnd-kit drag handlers
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);

    // Check if dragging a new widget from the picker
    const dragData = active.data.current;
    if (dragData?.type === 'new-widget') {
      setActiveDragData({
        type: 'new-widget',
        widgetType: dragData.widgetType,
        widgetDef: dragData.widgetDef,
      });
      // Close the modal immediately so user can see where they're dragging to
      setShowWidgetPicker(false);
    } else {
      setActiveDragData({ type: 'existing-widget' });
    }
  };

  // Helper to add a new widget at a specific location
  const addWidgetAtLocation = useCallback(async (
    widgetType: string,
    parentId: string | null,
    columnIndex: number = 0,
    sortOrder?: number
  ) => {
    const def = widgetDefs.find(w => w.id === widgetType);

    // Calculate sort order if not provided
    let finalSortOrder = sortOrder;
    if (finalSortOrder === undefined) {
      if (parentId) {
        const columnWidgets = getWidgetsByColumn(widgets, parentId, columnIndex);
        finalSortOrder = columnWidgets.length;
      } else {
        finalSortOrder = getMaxSortOrder();
      }
    }

    const newWidget = await addWidget(dashboardId, {
      widget_type: widgetType,
      grid_x: 0,
      grid_y: 0,
      grid_w: def?.default_width || 4,
      grid_h: def?.default_height || 2,
      sort_order: finalSortOrder,
      parent_widget_id: parentId,
      column_index: parentId ? columnIndex : undefined,
    });

    if (newWidget) {
      setWidgets(prev => [...prev, newWidget]);
      setShowWidgetPicker(false);
    }

    return newWidget;
  }, [dashboardId, widgetDefs, widgets, getMaxSortOrder]);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;

    // Capture the drag data BEFORE clearing state (the draggable may have unmounted)
    // Use our stored activeDragData state instead of active.data.current
    const currentDragData = activeDragData;

    setActiveId(null);
    setActiveDragData(null);

    if (!over) return;

    const overId = over.id as string;

    // Handle new widget drops from the picker
    if (currentDragData?.type === 'new-widget') {
      const widgetType = currentDragData.widgetType as string;

      // Check if dropped on a section header
      const sectionDropMatch = overId.match(/^section-drop-(.+)$/);
      if (sectionDropMatch) {
        const targetSectionId = sectionDropMatch[1];
        await addWidgetAtLocation(widgetType, targetSectionId, 0);
        return;
      }

      // Check if dropped on a column
      const columnMatch = overId.match(/^column-(.+)-(\d+)$/);
      if (columnMatch) {
        const targetSectionId = columnMatch[1];
        const targetColumnIndex = parseInt(columnMatch[2], 10);
        await addWidgetAtLocation(widgetType, targetSectionId, targetColumnIndex);
        return;
      }

      // Check if dropped on a widget that's inside a section
      const overWidget = widgets.find(w => w.id === overId);
      if (overWidget?.parent_widget_id) {
        // Widget is inside a section - add to that section's column
        const targetColumnIndex = overWidget.column_index ?? 0;
        await addWidgetAtLocation(widgetType, overWidget.parent_widget_id, targetColumnIndex);
        return;
      }

      // Check if dropped on a section widget itself (not its header)
      if (overWidget?.widget_type === 'section') {
        await addWidgetAtLocation(widgetType, overWidget.id, 0);
        return;
      }

      // Dropped on dashboard-root or any other area - add to root
      await addWidgetAtLocation(widgetType, null);
      return;
    }

    // Handle existing widget reordering
    if (active.id === over.id) return;

    const activeWidgetId = active.id as string;
    const activeWidget = widgets.find(w => w.id === activeWidgetId);

    if (!activeWidget) return;

    // Don't allow sections or spacers to be moved into sections
    if (activeWidget.widget_type === 'section' || activeWidget.widget_type === 'spacer') {
      // Only allow reordering at root level
      if (!activeWidget.parent_widget_id) {
        const oldIndex = rootWidgets.findIndex(w => w.id === activeWidgetId);
        const newIndex = rootWidgets.findIndex(w => w.id === overId);

        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          const reordered = arrayMove(rootWidgets, oldIndex, newIndex);
          const updates = reordered.map((w, idx) => ({ id: w.id, sort_order: idx }));
          await handleReorderWidgets(updates);
        }
      }
      return;
    }

    // Check if dropped on a section header (cross-section move)
    const sectionDropMatch = overId.match(/^section-drop-(.+)$/);
    if (sectionDropMatch) {
      const targetSectionId = sectionDropMatch[1];
      if (activeWidget.parent_widget_id !== targetSectionId) {
        await handleMoveToSection(activeWidgetId, targetSectionId, 0);
      }
      return;
    }

    // Check if dropped on a column within a section
    const columnMatch = overId.match(/^column-(.+)-(\d+)$/);
    if (columnMatch) {
      const targetSectionId = columnMatch[1];
      const targetColumnIndex = parseInt(columnMatch[2], 10);

      // Get widgets in target column to determine sort order
      const targetColumnWidgets = getWidgetsByColumn(widgets, targetSectionId, targetColumnIndex);
      const newSortOrder = targetColumnWidgets.length;

      if (activeWidget.parent_widget_id === targetSectionId) {
        // Same section - just update column and sort order
        await handleReorderWidgets([
          { id: activeWidgetId, sort_order: newSortOrder, column_index: targetColumnIndex }
        ]);
      } else {
        // Different section or from root - move to section and column
        await handleMoveToSection(activeWidgetId, targetSectionId, targetColumnIndex);
      }
      return;
    }

    // Check if dropped on dashboard-root (remove from section)
    if (overId === 'dashboard-root' && activeWidget.parent_widget_id) {
      await handleMoveToSection(activeWidgetId, null);
      return;
    }

    // Check if dropped on another widget
    const overWidget = widgets.find(w => w.id === overId);
    if (overWidget) {
      const activeParentId = activeWidget.parent_widget_id;
      const overParentId = overWidget.parent_widget_id;

      // Both widgets in same section - reorder within section
      if (activeParentId && activeParentId === overParentId) {
        const activeColumn = activeWidget.column_index ?? 0;
        const overColumn = overWidget.column_index ?? 0;

        if (activeColumn === overColumn) {
          // Same column - reorder
          const columnWidgets = getWidgetsByColumn(widgets, activeParentId, activeColumn);
          const oldIndex = columnWidgets.findIndex(w => w.id === activeWidgetId);
          const newIndex = columnWidgets.findIndex(w => w.id === overId);

          if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
            const reordered = arrayMove(columnWidgets, oldIndex, newIndex);
            const updates = reordered.map((w, idx) => ({ id: w.id, sort_order: idx }));
            await handleReorderWidgets(updates);
          }
        } else {
          // Different column in same section - move to that column
          const targetColumnWidgets = getWidgetsByColumn(widgets, activeParentId, overColumn);
          const targetIndex = targetColumnWidgets.findIndex(w => w.id === overId);
          const newSortOrder = targetIndex >= 0 ? targetIndex : targetColumnWidgets.length;

          await handleReorderWidgets([
            { id: activeWidgetId, sort_order: newSortOrder, column_index: overColumn }
          ]);
        }
        return;
      }

      // Widget in section dropped on widget in different section
      if (overParentId && activeParentId !== overParentId) {
        const targetColumn = overWidget.column_index ?? 0;
        await handleMoveToSection(activeWidgetId, overParentId, targetColumn);
        return;
      }

      // Root widget dropped on root widget - reorder
      if (!activeParentId && !overParentId) {
        const oldIndex = rootWidgets.findIndex(w => w.id === activeWidgetId);
        const newIndex = rootWidgets.findIndex(w => w.id === overId);

        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          const reordered = arrayMove(rootWidgets, oldIndex, newIndex);
          const updates = reordered.map((w, idx) => ({ id: w.id, sort_order: idx }));
          await handleReorderWidgets(updates);
        }
        return;
      }

      // Widget from section dropped on root widget - remove from section
      if (activeParentId && !overParentId) {
        await handleMoveToSection(activeWidgetId, null);
        return;
      }

      // Root widget dropped on widget in section - move to that section
      if (!activeParentId && overParentId) {
        const targetColumn = overWidget.column_index ?? 0;
        await handleMoveToSection(activeWidgetId, overParentId, targetColumn);
        return;
      }
    }

    // Fallback: Reordering root widgets
    if (!activeWidget.parent_widget_id) {
      const oldIndex = rootWidgets.findIndex(w => w.id === activeWidgetId);
      const newIndex = rootWidgets.findIndex(w => w.id === overId);

      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const reordered = arrayMove(rootWidgets, oldIndex, newIndex);
        const updates = reordered.map((w, idx) => ({ id: w.id, sort_order: idx }));
        await handleReorderWidgets(updates);
      }
    }
  }, [widgets, rootWidgets, handleMoveToSection, handleReorderWidgets, activeDragData, addWidgetAtLocation]);

  // Get the currently dragged widget for the overlay
  const activeWidget = activeId ? widgets.find(w => w.id === activeId) : null;

  // Toggle fullscreen
  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      const element = containerRef.current;
      if (element) {
        const computedStyle = getComputedStyle(document.documentElement);
        const bgColor = computedStyle.getPropertyValue('--ion-background-color').trim() || '#ffffff';
        const textColor = computedStyle.getPropertyValue('--ion-text-color').trim() || '#000000';
        element.style.setProperty('--fullscreen-bg', bgColor);
        element.style.setProperty('--fullscreen-text', textColor);
        await element.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Refresh dashboard data
  const handleRefresh = async () => {
    setLoading(true);
    const data = await getDashboardFull(dashboardId);
    if (data) {
      setDashboard(data.dashboard);
      setWidgets(data.widgets);
    }
    setLoading(false);
  };

  // View mode FAB actions
  const viewModeFabActions: FABAction[] = [
    {
      id: 'refresh',
      icon: refreshOutline,
      label: 'Refresh',
      onClick: handleRefresh,
    },
    {
      id: 'fullscreen',
      icon: isFullscreen ? contractOutline : expandOutline,
      label: isFullscreen ? 'Exit Fullscreen' : 'Fullscreen',
      onClick: toggleFullscreen,
    },
    {
      id: 'announcement',
      icon: megaphoneOutline,
      label: 'Announcement',
      onClick: () => setShowAnnouncementModal(true),
    },
    {
      id: 'chore',
      icon: checkmarkCircle,
      label: 'Add Chore',
      onClick: () => setShowChoreModal(true),
    },
    {
      id: 'grocery',
      icon: cartOutline,
      label: 'Add Grocery',
      onClick: () => setShowGroceryModal(true),
    },
  ];

  // Background style
  const getBackgroundStyle = (): React.CSSProperties => {
    const themeBackground = { backgroundColor: 'var(--fullscreen-bg, var(--ion-background-color))' };

    if (!dashboard) return themeBackground;

    const legacyDarkDefaults = ['#0f172a', '#1a1a1a', '#121212', '#000000'];
    const hasCustomBackground = dashboard.background_type &&
      dashboard.background_value &&
      !legacyDarkDefaults.includes(dashboard.background_value.toLowerCase());

    if (!hasCustomBackground) return themeBackground;

    switch (dashboard.background_type) {
      case 'color':
        return { backgroundColor: dashboard.background_value };
      case 'gradient':
        return { background: dashboard.background_value };
      case 'image':
        return {
          backgroundImage: `url(${dashboard.background_value})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        };
      default:
        return themeBackground;
    }
  };

  // Loading state
  if (loading) {
    return (
      <PageShell title="Loading..." showBack onBack={onSettingsClick}>
        <div className="dashboard-loading">
          <IonSpinner />
          <p>Loading dashboard...</p>
        </div>
      </PageShell>
    );
  }

  // Error state
  if (!dashboard) {
    return (
      <PageShell title="Dashboard" showBack onBack={onSettingsClick}>
        <div className="dashboard-error">
          <p>Dashboard not found</p>
        </div>
      </PageShell>
    );
  }

  // Header action buttons
  const headerActions = (
    <>
      <button
        className="dashboard-header-icon-btn"
        onClick={handleEditModeToggle}
        aria-label={editMode ? 'Done editing' : 'Edit dashboard'}
      >
        <IonIcon icon={editMode ? checkmarkOutline : pencilOutline} />
      </button>
      <button
        className="dashboard-header-icon-btn"
        onClick={toggleFullscreen}
        aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
      >
        <IonIcon icon={isFullscreen ? contractOutline : expandOutline} />
      </button>
    </>
  );

  const rootWidgetIds = rootWidgets.map(w => w.id);

  return (
    <PageShell
      title={`${dashboard.icon} ${dashboard.name}`}
      headerType="page"
      showBack
      onBack={onSettingsClick}
      rightButtons={headerActions}
      disableContentScroll
      noPadding
    >
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div
          ref={containerRef}
          className={`dashboard-container ${editMode ? 'edit-mode' : ''} ${isFullscreen ? 'fullscreen' : ''}`}
          style={getBackgroundStyle()}
        >
          {/* Root vertical stack */}
          <div
            ref={setRootDropRef}
            className={`dashboard-root ${isOverRoot && activeId ? 'drop-zone-active' : ''}`}
          >
            <SortableContext items={rootWidgetIds} strategy={verticalListSortingStrategy}>
              {rootWidgets.map(widget => {
                const isSection = widget.widget_type === 'section';
                const isExpanded = !collapsedSections.has(widget.id);

                if (isSection) {
                  // Render section with its own DndContext
                  return (
                    <SortableWidget
                      key={widget.id}
                      widget={widget}
                      householdId={householdId}
                      editMode={editMode}
                      onDeleteWidget={handleDeleteWidget}
                      onUpdateWidget={handleUpdateWidget}
                      onConfigureWidget={handleConfigureWidget}
                      onMoveToSection={handleMoveToSection}
                      onExpandWidget={handleExpandWidget}
                      availableSections={availableSections}
                      allWidgets={widgets}
                      isInSection={false}
                    >
                      <SectionWidget
                        widget={widget}
                        householdId={householdId}
                        editMode={editMode}
                        allWidgets={widgets}
                        isExpanded={isExpanded}
                        onToggleCollapse={(expanded) => handleToggleSection(widget.id, expanded)}
                        isDropTarget={editMode}
                        isPotentialDropTarget={activeId !== null && activeId !== widget.id}
                        onUpdateWidget={handleUpdateWidget}
                        onDeleteWidget={handleDeleteWidget}
                        onConfigureWidget={handleConfigureWidget}
                        onMoveToSection={handleMoveToSection}
                        onReorderWidgets={handleReorderWidgets}
                        onExpandWidget={handleExpandWidget}
                        availableSections={availableSections}
                      />
                    </SortableWidget>
                  );
                }

                // Regular widget
                return (
                  <SortableWidget
                    key={widget.id}
                    widget={widget}
                    householdId={householdId}
                    editMode={editMode}
                    onDeleteWidget={handleDeleteWidget}
                    onUpdateWidget={handleUpdateWidget}
                    onConfigureWidget={handleConfigureWidget}
                    onMoveToSection={handleMoveToSection}
                    onExpandWidget={handleExpandWidget}
                    availableSections={availableSections}
                    allWidgets={widgets}
                    isInSection={false}
                  />
                );
              })}
            </SortableContext>

            {/* Empty state */}
            {rootWidgets.length === 0 && (
              <div className="dashboard-empty">
                <p>No widgets yet</p>
                {editMode && <p>Click + to add widgets</p>}
              </div>
            )}
          </div>

          {/* Speed Dial FAB - only in edit mode */}
          {editMode && (
            <IonFab vertical="bottom" horizontal="end" slot="fixed" className="edit-mode-fab">
              <IonFabButton>
                <IonIcon icon={add} />
              </IonFabButton>
              <IonFabList side="top">
                <IonFabButton onClick={() => setShowWidgetPicker(true)}>
                  <span className="fab-button-label">Add Widget</span>
                  <IonIcon icon={appsOutline} />
                </IonFabButton>
                <IonFabButton onClick={() => handleAddLayoutWidget('section')}>
                  <span className="fab-button-label">Add Section</span>
                  <IonIcon icon={reorderFourOutline} />
                </IonFabButton>
                <IonFabButton onClick={() => handleAddLayoutWidget('spacer')}>
                  <span className="fab-button-label">Add Spacer</span>
                  <IonIcon icon={removeOutline} />
                </IonFabButton>
              </IonFabList>
            </IonFab>
          )}

          {/* Edit Mode Overlay */}
          {editMode && (
            <div className="edit-mode-indicator">
              <span>Edit Mode</span>
              <p>Drag widgets to reorder</p>
            </div>
          )}

          {/* View Mode FAB - quick actions */}
          {!editMode && (
            <DashboardFAB
              actions={viewModeFabActions}
              position="bottom-right"
              showLabels
            />
          )}
        </div>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeId && activeDragData ? (
            <div className="drag-overlay-widget">
              <div className="drag-overlay-content">
                {activeDragData.type === 'new-widget' && activeDragData.widgetDef ? (
                  <>
                    <span>{activeDragData.widgetDef.icon}</span>
                    <span>{activeDragData.widgetDef.name}</span>
                  </>
                ) : activeWidget ? (
                  activeWidget.title || activeWidget.widget_type
                ) : null}
              </div>
            </div>
          ) : null}
        </DragOverlay>

      {/* Widget Picker Modal */}
      <Modal
        isOpen={showWidgetPicker}
        onClose={() => setShowWidgetPicker(false)}
        size="lg"
        header={{ title: 'Add Widget' }}
      >
        <ModalSection spacing="tight">
          <Search
            value={widgetSearch}
            onChange={setWidgetSearch}
            placeholder="Search widgets..."
            variant="filled"
            size="md"
          />
        </ModalSection>

        <div style={{ padding: '0 16px 16px', overflowX: 'auto' }}>
          <Segment
            options={WIDGET_CATEGORY_OPTIONS}
            value={widgetCategory}
            onChange={setWidgetCategory}
            size="sm"
          />
        </div>

        <p className="widget-picker-hint">
          Click to add to dashboard, or drag to a specific location
        </p>

        <div className="widget-picker-grid">
          {filteredWidgets.map(widget => (
            <DraggableWidgetOption
              key={widget.id}
              widget={widget}
              onClick={() => handleAddWidget(widget.id)}
            />
          ))}
          {filteredWidgets.length === 0 && (
            <p style={{ padding: 16, color: 'var(--ion-color-medium)', textAlign: 'center' }}>
              No widgets found
            </p>
          )}
        </div>
      </Modal>
      </DndContext>

      {/* Delete Confirmation Alert */}
      <IonAlert
        isOpen={deleteAlert.isOpen}
        onDidDismiss={() => setDeleteAlert({ isOpen: false, widgetId: null })}
        header="Delete Widget"
        message="Are you sure you want to delete this widget?"
        buttons={[
          { text: 'Cancel', role: 'cancel' },
          { text: 'Delete', role: 'destructive', handler: confirmDeleteWidget },
        ]}
      />

      {/* Widget Configuration Modal */}
      <Modal
        isOpen={!!configWidget}
        onClose={() => setConfigWidget(null)}
        size="md"
        header={{ title: `Configure ${configWidget?.widget_type || 'Widget'}` }}
      >
        {configWidget && (
          <WidgetConfigForm
            widget={configWidget}
            onSave={handleSaveConfig}
            onCancel={() => setConfigWidget(null)}
          />
        )}
      </Modal>

      {/* Quick Action Modals (from View Mode FAB) */}
      <Modal
        isOpen={showAnnouncementModal}
        onClose={() => setShowAnnouncementModal(false)}
        size="md"
        header={{ title: 'New Announcement' }}
      >
        <ModalSection>
          <p style={{ color: 'var(--ion-color-medium)', textAlign: 'center' }}>
            Announcement creation coming soon.
          </p>
        </ModalSection>
      </Modal>

      <Modal
        isOpen={showChoreModal}
        onClose={() => setShowChoreModal(false)}
        size="md"
        header={{ title: 'Quick Add Chore' }}
      >
        <ModalSection>
          <p style={{ color: 'var(--ion-color-medium)', textAlign: 'center' }}>
            Quick chore assignment coming soon.
          </p>
        </ModalSection>
      </Modal>

      <Modal
        isOpen={showGroceryModal}
        onClose={() => setShowGroceryModal(false)}
        size="md"
        header={{ title: 'Add Grocery Item' }}
      >
        <ModalSection>
          <p style={{ color: 'var(--ion-color-medium)', textAlign: 'center' }}>
            Quick grocery item addition coming soon.
          </p>
        </ModalSection>
      </Modal>

      {/* Widget Fullscreen Overlay */}
      <WidgetFullscreenOverlay
        isOpen={!!fullscreenWidget}
        onClose={() => setFullscreenWidgetId(null)}
        widget={fullscreenWidget}
        householdId={householdId}
      />
    </PageShell>
  );
};

export default DashboardDisplay;
